import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, BookOpen, Upload, X, Sparkles, Loader2, Search, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURATED_AUTHORS = [
  'Eric Carle', 'Mo Willems', 'Julia Donaldson', 'Sandra Boynton', 'Karen Katz',
  'Bill Martin Jr.', 'Margaret Wise Brown', 'Dr. Seuss', 'Roald Dahl', 'Leslie Patricelli',
];

function SortableBookRow({ book, onEdit, onDelete, dragEnabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: book.id,
    disabled: !dragEnabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <tr ref={setNodeRef} style={style} className="border-t border-border hover:bg-muted/50 bg-card" data-testid={`book-row-${book.id}`}>
      <td className="p-4 w-10">
        <button
          type="button"
          className={`touch-none ${dragEnabled ? 'cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground' : 'cursor-not-allowed opacity-30'}`}
          {...attributes}
          {...listeners}
          data-testid={`book-drag-${book.id}`}
          title={dragEnabled ? 'Drag to reorder' : 'Clear search to reorder'}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="p-4 font-body">{book.title}</td>
      <td className="p-4 font-body text-muted-foreground">{book.author || '—'}</td>
      <td className="p-4 font-body text-sm">{book.age_range || '—'}</td>
      <td className="p-4 font-body text-sm">{book.genre || '—'}</td>
      <td className="p-4 text-right">
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onEdit(book)} data-testid={`book-edit-${book.id}`}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(book.id)} data-testid={`book-delete-${book.id}`}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminBooks() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [fetchUrl, setFetchUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    illustrator: '',
    is_indian_author: false,
    age_range: '',
    description: '',
    why_it_matters: '',
    themes: '',
    genre: '',
    format: '',
    occasion_tags: '',
    image_url: '',
    buy_link: '',
  });

  const authorOptions = Array.from(
    new Set([...CURATED_AUTHORS, ...books.map((b) => b.author).filter(Boolean)])
  ).sort();

  const q = searchQuery.trim().toLowerCase();
  const filteredBooks = q
    ? books.filter((b) => [b.title, b.author, b.illustrator].some((v) => v && v.toLowerCase().includes(q)))
    : books;
  const isReorderable = !q;

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = books.findIndex((b) => b.id === active.id);
    const newIndex = books.findIndex((b) => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newBooks = arrayMove(books, oldIndex, newIndex);
    setBooks(newBooks);
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/books/reorder`, { ordered_ids: newBooks.map((b) => b.id) }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Order saved — this is how books appear on the site');
    } catch (error) {
      toast.error('Failed to save new order');
      fetchBooks();
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/books`);
      setBooks(response.data);
    } catch (error) {
      console.error('Error fetching books:', error);
      toast.error('Failed to load books');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    const bookData = {
      ...formData,
      themes: formData.themes.split(',').map(t => t.trim()).filter(Boolean),
      occasion_tags: formData.occasion_tags.split(',').map(t => t.trim()).filter(Boolean),
    };

    try {
      if (editingBook) {
        await axios.put(`${API}/books/${editingBook.id}`, bookData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Book updated successfully');
      } else {
        await axios.post(`${API}/books`, bookData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Book added successfully');
      }
      fetchBooks();
      handleCloseDialog();
    } catch (error) {
      toast.error('Failed to save book');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this book?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/books/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Book deleted successfully');
      fetchBooks();
    } catch (error) {
      toast.error('Failed to delete book');
    }
  };

  const handleEdit = (book) => {
    setEditingBook(book);
    setFormData({
      ...book,
      illustrator: book.illustrator || '',
      is_indian_author: book.is_indian_author || false,
      themes: book.themes.join(', '),
      occasion_tags: book.occasion_tags.join(', '),
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBook(null);
    setFetchUrl('');
    setFormData({
      title: '',
      author: '',
      illustrator: '',
      is_indian_author: false,
      age_range: '',
      description: '',
      why_it_matters: '',
      themes: '',
      genre: '',
      format: '',
      occasion_tags: '',
      image_url: '',
      buy_link: '',
    });
  };

  const handleFetchMetadata = async () => {
    if (!fetchUrl.trim()) {
      toast.error('Paste a product link first');
      return;
    }
    const token = localStorage.getItem('admin_token');
    setFetching(true);
    try {
      const { data } = await axios.post(`${API}/admin/fetch-metadata`, { url: fetchUrl.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFormData((prev) => ({
        ...prev,
        buy_link: data.buy_link || prev.buy_link,
        image_url: data.image_url || prev.image_url,
        title: prev.title || data.title || '',
        description: prev.description || data.description || '',
      }));
      toast.success('Details fetched! Review and adjust before saving.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not fetch details from that link');
    } finally {
      setFetching(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('admin_token');
    const formDataImg = new FormData();
    formDataImg.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formDataImg, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setFormData({ ...formData, image_url: response.data.image_url });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Image upload failed');
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will add sample books. Continue?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/admin/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Sample data added!');
      fetchBooks();
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };

  const handleClearData = async () => {
    if (!confirm('This will permanently delete ALL books and gifts so you can start fresh. Continue?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/admin/clear-data`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('All data cleared. Start adding your curated items!');
      fetchBooks();
    } catch (error) {
      toast.error('Failed to clear data');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-heading text-4xl font-light text-primary" data-testid="admin-books-title">
          Manage Books
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSeedData} data-testid="admin-books-seed-button">
            Add Sample Data
          </Button>
          <Button variant="outline" className="text-destructive" onClick={handleClearData} data-testid="admin-books-clear-button">
            Clear All Data
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingBook(null)} data-testid="admin-books-add-button">
                <Plus className="mr-2 h-4 w-4" />
                Add Book
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingBook ? 'Edit Book' : 'Add New Book'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 space-y-2" data-testid="book-autofill-box">
                  <label className="font-body text-sm font-semibold flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-accent" />
                    Auto-fill from a product link
                  </label>
                  <p className="text-xs text-muted-foreground">Paste an Amazon or store link — we'll pull the title, cover image and description automatically.</p>
                  <div className="flex gap-2">
                    <Input
                      value={fetchUrl}
                      onChange={(e) => setFetchUrl(e.target.value)}
                      placeholder="https://www.amazon.in/..."
                      data-testid="book-autofill-input"
                    />
                    <Button type="button" onClick={handleFetchMetadata} disabled={fetching} data-testid="book-autofill-button">
                      {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Fetch'}
                    </Button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-sm font-medium mb-1 block">Title *</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      data-testid="book-form-title"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm font-medium mb-1 block">Author</label>
                    <Input
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      list="author-options"
                      placeholder="Type or pick an author"
                      data-testid="book-form-author"
                    />
                    <datalist id="author-options">
                      {authorOptions.map((a) => (
                        <option key={a} value={a} />
                      ))}
                    </datalist>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-sm font-medium mb-1 block">Illustrated by</label>
                    <Input
                      value={formData.illustrator}
                      onChange={(e) => setFormData({ ...formData, illustrator: e.target.value })}
                      placeholder="Illustrator name"
                      data-testid="book-form-illustrator"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer pb-2" htmlFor="book-form-indian">
                      <Checkbox
                        id="book-form-indian"
                        checked={formData.is_indian_author}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_indian_author: !!checked })}
                        data-testid="book-form-indian-author"
                      />
                      <span className="font-body text-sm font-medium">Indian author</span>
                    </label>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="font-body text-sm font-medium mb-1 block">Age Range</label>
                    <Select value={formData.age_range} onValueChange={(value) => setFormData({ ...formData, age_range: value })}>
                      <SelectTrigger data-testid="book-form-age">
                        <SelectValue placeholder="Select age range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0-6 months">0-6 months</SelectItem>
                        <SelectItem value="6 months-1 year">6 months-1 year</SelectItem>
                        <SelectItem value="1+ years">1+ years</SelectItem>
                        <SelectItem value="2+ years">2+ years</SelectItem>
                        <SelectItem value="3+ years">3+ years</SelectItem>
                        <SelectItem value="4+ years">4+ years</SelectItem>
                        <SelectItem value="0-5 years">0-5 years</SelectItem>
                        <SelectItem value="6+ years">6+ years</SelectItem>
                        <SelectItem value="9+ years">9+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-body text-sm font-medium mb-1 block">Genre</label>
                    <Select value={formData.genre} onValueChange={(value) => setFormData({ ...formData, genre: value })}>
                      <SelectTrigger data-testid="book-form-genre">
                        <SelectValue placeholder="Select genre" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Picture Book">Picture Book</SelectItem>
                        <SelectItem value="Board Book">Board Book</SelectItem>
                        <SelectItem value="Chapter Book">Chapter Book</SelectItem>
                        <SelectItem value="Fiction">Fiction</SelectItem>
                        <SelectItem value="Non-Fiction">Non-Fiction</SelectItem>
                        <SelectItem value="Potty Training">Potty Training</SelectItem>
                        <SelectItem value="Grief">Grief</SelectItem>
                        <SelectItem value="Science">Science</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-body text-sm font-medium mb-1 block">Format</label>
                    <Select value={formData.format} onValueChange={(value) => setFormData({ ...formData, format: value })}>
                      <SelectTrigger data-testid="book-form-format">
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Board Book">Board Book</SelectItem>
                        <SelectItem value="Hardcover">Hardcover</SelectItem>
                        <SelectItem value="Paperback">Paperback</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="font-body text-sm font-medium mb-1 block">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    data-testid="book-form-description"
                  />
                </div>

                <div>
                  <label className="font-body text-sm font-medium mb-1 block">Why It Matters</label>
                  <Textarea
                    value={formData.why_it_matters}
                    onChange={(e) => setFormData({ ...formData, why_it_matters: e.target.value })}
                    rows={3}
                    data-testid="book-form-why-matters"
                  />
                </div>

                <div>
                  <label className="font-body text-sm font-medium mb-1 block">Themes (comma-separated)</label>
                  <Input
                    value={formData.themes}
                    onChange={(e) => setFormData({ ...formData, themes: e.target.value })}
                    placeholder="Growth, Emotions, Adventure"
                    data-testid="book-form-themes"
                  />
                </div>

                <div>
                  <label className="font-body text-sm font-medium mb-1 block">Occasion Tags (comma-separated)</label>
                  <Input
                    value={formData.occasion_tags}
                    onChange={(e) => setFormData({ ...formData, occasion_tags: e.target.value })}
                    placeholder="Bedtime, Birthday, Any Time"
                    data-testid="book-form-occasions"
                  />
                </div>

                <div>
                  <label className="font-body text-sm font-medium mb-1 block">Image</label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="Image URL or upload below"
                      data-testid="book-form-image-url"
                    />
                    <label className="cursor-pointer">
                      <Button type="button" variant="outline" asChild>
                        <span>
                          <Upload className="h-4 w-4" />
                        </span>
                      </Button>
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    </label>
                  </div>
                </div>

                <div>
                  <label className="font-body text-sm font-medium mb-1 block">Buy Link</label>
                  <Input
                    value={formData.buy_link}
                    onChange={(e) => setFormData({ ...formData, buy_link: e.target.value })}
                    placeholder="https://..."
                    data-testid="book-form-buy-link"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={handleCloseDialog} data-testid="book-form-cancel">
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="book-form-submit">
                    {editingBook ? 'Update' : 'Add'} Book
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search books by title, author or illustrator..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="admin-books-search"
          />
        </div>
        <p className="text-xs text-muted-foreground" data-testid="admin-books-count">
          {isReorderable
            ? `${books.length} books · drag the handle to reorder`
            : `${filteredBooks.length} of ${books.length} shown · clear search to reorder`}
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading books...</p>
        </div>
      ) : books.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4" data-testid="admin-books-empty">No books yet. Add your first book!</p>
        </div>
      ) : filteredBooks.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground" data-testid="admin-books-no-results">No books match "{searchQuery}".</p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <table className="w-full" data-testid="admin-books-table">
                <thead className="bg-muted">
                  <tr>
                    <th className="w-10 p-4"></th>
                    <th className="text-left p-4 font-body text-sm font-semibold">Title</th>
                    <th className="text-left p-4 font-body text-sm font-semibold">Author</th>
                    <th className="text-left p-4 font-body text-sm font-semibold">Age Range</th>
                    <th className="text-left p-4 font-body text-sm font-semibold">Genre</th>
                    <th className="text-right p-4 font-body text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <SortableContext items={filteredBooks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                  <tbody>
                    {filteredBooks.map((book) => (
                      <SortableBookRow
                        key={book.id}
                        book={book}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        dragEnabled={isReorderable}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </table>
            </DndContext>
          </div>
        </div>
      )}
    </div>
  );
}
